import * as React from "react";
import { animate, createTimeline, stagger, utils } from "animejs";
import {
  ArrowUpIcon,
  CheckIcon,
  FileTextIcon,
  GlobeHemisphereWestIcon,
  PaperclipIcon,
  PencilSimpleIcon,
  WarningIcon,
  XIcon,
  FilePlusIcon,
  type Icon
} from "@phosphor-icons/react";
import { cn } from "cn";

import {
  Attachment,
  AttachmentContent,
  AttachmentMedia,
  AttachmentTitle
} from "@libs/zapi-ui/ds/components/attachment";
import { Bubble, BubbleContent } from "@libs/zapi-ui/ds/components/bubble";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { Replies, Reply } from "@libs/zapi-ui/ds/components/reply";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea
} from "@libs/zapi-ui/ds/components/input-group";
import { Kbd, KbdGroup } from "@libs/zapi-ui/ds/components/kbd";
import { LeoGlyph, LeoWordmark } from "@libs/zapi-ui/ds/components/leo-wordmark";
import { Message, MessageAvatar, MessageContent } from "@libs/zapi-ui/ds/components/message";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport
} from "@libs/zapi-ui/ds/components/message-scroller";
import { ScrambleText } from "@libs/zapi-ui/ds/components/scramble-text";
import { SidePanel } from "@libs/zapi-ui/ds/components/side-panel";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Stagger } from "@libs/zapi-ui/ds/components/stagger";
import { TextReveal } from "@libs/zapi-ui/ds/components/text-reveal";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { useMotionScope } from "@/zapi-frontend/shared/hooks/useMotionScope";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/ds/components/tooltip";
import { displayLink } from "@/zapi-frontend/shared/lib/link";
import { duration, ease, prefersReducedMotion, staggerStep } from "@libs/zapi-ui/lib/motion";

type LeoAction = {
  id: string;
  label: string;
  primary?: boolean;
  icon?: Icon;
  intent?: "reply" | "compose";
  subtle?: boolean;
};

type LeoStep = { label: string; status: "done" | "active" };

type LeoMessage = {
  id: string;
  role: "user" | "leo";
  content: string;
  flag?: string;
  // A titled message is a greeting; once answered it folds into an ordinary message that says its question.
  title?: string;
  question?: string;
  issue?: { label: string; icon?: Icon };
  steps?: LeoStep[];
  attachments?: string[];
  actions?: LeoAction[];
  answered?: boolean;
};

type LeoActionHandler = (message: LeoMessage, action: LeoAction) => void;

type LeoStatus = "idle" | "thinking";

type LeoLabels = {
  panel: string;
  name: string;
  close: string;
  hide: string;
  closeOverlay: string;
  ask: string;
  askWaiting: string;
  waiting: string;
  dismiss: string;
  welcomeTitle: string;
  welcomeDescription: string;
  conversation: string;
  placeholder: string;
  message: string;
  attach: string;
  attachFile: string;
  files: string;
  webLink: string;
  send: string;
  disclaimer: string;
  thinking: string;
  done: string;
  more: (count: number) => string;
  scrollToEnd: string;
};

const defaultLeoLabels: LeoLabels = {
  panel: "Leo, Innovera intelligence",
  name: "Leo",
  close: "Close Leo",
  hide: "Hide Leo",
  closeOverlay: "Close",
  ask: "Ask Leo",
  askWaiting: "Ask Leo, waiting for your reply",
  waiting: "Leo is waiting for you",
  dismiss: "Dismiss",
  welcomeTitle: "How can I help today?",
  welcomeDescription: "Ask about your projects, sources and reports.",
  conversation: "Conversation with Leo",
  placeholder: "Ask Leo anything",
  message: "Message Leo",
  attach: "Attach",
  attachFile: "Attach a file",
  files: "Files",
  webLink: "Web Link",
  send: "Send",
  disclaimer: "Leo can make mistakes. Check important findings.",
  thinking: "Thinking",
  done: "Done",
  more: (count) => `+${count} more`,
  scrollToEnd: "Scroll to end"
};

function leoLabels(labels?: Partial<LeoLabels>): LeoLabels {
  return { ...defaultLeoLabels, ...labels };
}

const THINKING_STEP_MS = 1300;
const ENTER_CONTENT_DELAY_MS = 260;
const MOBILE_TAKEOVER_QUERY = "(max-width: 1023.98px)";
const FEATHER_ALPHA_STOPS = [
  [0, 0.92],
  [12, 0.88],
  [26, 0.78],
  [40, 0.63],
  [54, 0.46],
  [68, 0.29],
  [81, 0.15],
  [92, 0.05],
  [100, 0]
] as const;
const FEATHER_WIDTH = "12rem";
const FADE_INTO_PANEL = "72px";
const FADE_LENGTH = `(${FEATHER_WIDTH} + ${FADE_INTO_PANEL})`;

function featherGradient(side: "left" | "right", color: (alpha: number) => string) {
  const stops = FEATHER_ALPHA_STOPS.map(
    ([at, alpha]) => `${color(alpha)} calc(100% - ${FADE_LENGTH} + ${at / 100} * ${FADE_LENGTH})`
  );
  return `linear-gradient(to ${side}, ${color(FEATHER_ALPHA_STOPS[0][1])} 0%, ${stops.join(", ")})`;
}

const tint = (alpha: number) => `color-mix(in oklch, var(--color-canvas) ${alpha * 100}%, transparent)`;
const mask = (alpha: number) => `rgb(0 0 0 / ${alpha / FEATHER_ALPHA_STOPS[0][1]})`;

const featherStyle = {
  "--leo-tint": featherGradient("left", tint),
  "--leo-tint-rtl": featherGradient("right", tint),
  "--leo-mask": featherGradient("left", mask),
  "--leo-mask-rtl": featherGradient("right", mask)
} as React.CSSProperties;

function LeoAvatar({ className }: { className?: string }) {
  return (
    <span data-slot="leo-avatar" className={cn("flex size-8 shrink-0 items-center justify-center text-leo", className)}>
      <LeoGlyph className="size-[55%]" title="" />
    </span>
  );
}

function LeoThinking({ steps, labels }: { steps: string[]; labels?: Partial<LeoLabels> }) {
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (steps.length < 2) return;
    const interval = window.setInterval(() => setIndex((current) => (current + 1) % steps.length), THINKING_STEP_MS);
    return () => window.clearInterval(interval);
  }, [steps.length]);

  return (
    <Message aria-live="polite">
      <MessageAvatar className="self-start bg-transparent">
        <LeoAvatar />
      </MessageAvatar>
      <MessageContent>
        <span className="typo-label-sm flex h-8 items-center font-mono text-muted-foreground">
          <ScrambleText text={steps[index] ?? leoLabels(labels).thinking} charset="uppercase" />
        </span>
      </MessageContent>
    </Message>
  );
}

function LeoWelcome({
  labels,
  title = leoLabels(labels).welcomeTitle,
  description = leoLabels(labels).welcomeDescription,
  prompt,
  className,
  children
}: {
  className?: string;
  title?: string;
  description?: React.ReactNode;
  prompt?: React.ReactNode;
  labels?: Partial<LeoLabels>;
  children: React.ReactNode;
}) {
  return (
    <div data-slot="leo-welcome" className={cn("flex h-full flex-col gap-5", className)}>
      <div className="flex flex-1 flex-col items-center justify-center gap-1.5 text-center">
        <TextReveal as="h2" className="typo-h5" delay={ENTER_CONTENT_DELAY_MS + 120}>
          {title}
        </TextReveal>
        {description && <p className="typo-body-sm whitespace-pre-line text-muted-foreground">{description}</p>}
      </div>
      <div className="flex flex-col gap-2">
        {prompt && <p className="typo-body-sm text-muted-foreground">{prompt}</p>}
        <Stagger
          delay={ENTER_CONTENT_DELAY_MS + 420}
          step={staggerStep.base}
          className="flex flex-col items-start gap-1.5"
        >
          {children}
        </Stagger>
      </div>
    </div>
  );
}

function LeoWelcomePill({
  icon: PillIcon,
  onClick,
  disabled,
  subtle,
  children
}: {
  icon?: Icon;
  onClick: () => void;
  disabled?: boolean;
  subtle?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Reply quiet={subtle} disabled={disabled} onClick={onClick}>
      {PillIcon && <PillIcon data-icon="inline-start" />}
      {children}
    </Reply>
  );
}

const leoComposerClassNames = {
  group: "rounded-3xl bg-background shadow-xs [--color-ring:var(--color-grayscale-300)]",
  textarea: "typo-body-sm field-sizing-content max-h-48 min-h-12 resize-none px-4 pt-3.5",
  actions: "px-3 pb-3"
};

function LeoDisclaimer({
  labels,
  children = leoLabels(labels).disclaimer,
  className
}: {
  children?: React.ReactNode;
  className?: string;
  labels?: Partial<LeoLabels>;
}) {
  return <p className={cn("typo-caption text-center text-muted-foreground", className)}>{children}</p>;
}

function LeoUserBubble({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <Bubble align="end" variant="muted" className={className}>
      <BubbleContent className="rounded-3xl px-4 py-2.5 typo-body-sm">{children}</BubbleContent>
    </Bubble>
  );
}

function LeoPanelHeader({
  adornment,
  actions,
  onClose,
  labels,
  closeLabel = leoLabels(labels).close,
  closeHint,
  divider,
  className,
  ...props
}: Omit<React.ComponentProps<"header">, "children"> & {
  adornment?: React.ReactNode;
  actions?: React.ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  closeHint?: React.ReactNode;
  divider?: boolean;
  labels?: Partial<LeoLabels>;
}) {
  return (
    <header
      data-slot="leo-panel-header"
      className={cn(
        "flex h-14 shrink-0 items-center gap-2 border-b ps-4 pe-3 transition-colors duration-200",
        divider === undefined
          ? "border-transparent group-has-[[data-slot=message-scroller-viewport][data-scrollable~=start]]/leo-panel:border-border"
          : divider
          ? "border-border"
          : "border-transparent",
        className
      )}
      {...props}
    >
      <LeoWordmark className="h-4 text-leo" title={leoLabels(labels).name} />
      {adornment}
      <span className="flex-1" />
      {actions}
      {onClose && (
        <Tooltip>
          <TooltipTrigger
            render={<Button variant="ghost" size="icon-sm" shape="pill" aria-label={closeLabel} onClick={onClose} />}
          >
            <XIcon />
          </TooltipTrigger>
          <TooltipContent className="flex items-center gap-2">{closeHint ?? closeLabel}</TooltipContent>
        </Tooltip>
      )}
    </header>
  );
}

function LeoComposer({
  disabled,
  labels,
  placeholder = leoLabels(labels).placeholder,
  attachAccept,
  onSend,
  onAttach,
  onAddLink
}: {
  disabled: boolean;
  placeholder?: string;
  attachAccept?: string;
  labels?: Partial<LeoLabels>;
  onSend: (text: string) => void;
  onAttach?: (files: File[]) => void;
  onAddLink?: () => void;
}) {
  const text = leoLabels(labels);
  const [draft, setDraft] = React.useState("");
  const fileInput = React.useRef<HTMLInputElement>(null);
  const canSend = draft.trim().length > 0 && !disabled;

  const submit = () => {
    if (!canSend) return;
    onSend(draft.trim());
    setDraft("");
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <InputGroup className={leoComposerClassNames.group}>
        <InputGroupTextarea
          data-leo-composer=""
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          aria-label={text.message}
          rows={1}
          className={leoComposerClassNames.textarea}
        />
        <InputGroupAddon align="block-end" className={cn("justify-end", leoComposerClassNames.actions)}>
          {onAttach && (
            <>
              {onAddLink ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <InputGroupButton
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={text.attach}
                        className="me-auto rounded-full"
                      />
                    }
                  >
                    <PaperclipIcon />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    align="start"
                    sideOffset={8}
                    className="w-auto rounded-xl p-1.5 shadow-lg"
                  >
                    <DropdownMenuItem
                      className="gap-2.5 rounded-lg px-2.5 py-2 typo-label-md"
                      onClick={() => fileInput.current?.click()}
                    >
                      <FilePlusIcon className="size-[18px] text-muted-foreground" />
                      {text.files}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="gap-2.5 rounded-lg px-2.5 py-2 typo-label-md" onClick={onAddLink}>
                      <GlobeHemisphereWestIcon className="size-[18px] text-muted-foreground" />
                      {text.webLink}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <InputGroupButton
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label={text.attachFile}
                  className="me-auto rounded-full"
                  onClick={() => fileInput.current?.click()}
                >
                  <PaperclipIcon />
                </InputGroupButton>
              )}
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={attachAccept}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  event.target.value = "";
                  if (files.length > 0) onAttach(files);
                }}
              />
            </>
          )}
          <InputGroupButton
            type="submit"
            size="icon-sm"
            variant="default"
            disabled={!canSend}
            aria-label={text.send}
            className="rounded-full"
          >
            <ArrowUpIcon weight="bold" />
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
      <LeoDisclaimer labels={labels} />
    </form>
  );
}

const ATTACHMENTS_SHOWN = 3;

function LeoAttachments({ names, align, labels }: { names: string[]; align?: "end"; labels?: Partial<LeoLabels> }) {
  const [expanded, setExpanded] = React.useState(false);
  const hidden = expanded ? 0 : Math.max(0, names.length - ATTACHMENTS_SHOWN);
  const shown = hidden > 0 ? names.slice(0, ATTACHMENTS_SHOWN) : names;

  return (
    <div data-slot="leo-attachments" className={cn("flex min-w-0 flex-col gap-1.5", align === "end" && "items-end")}>
      {shown.map((name, index) => {
        const link = /^https?:\/\//i.test(name);
        const AttachmentIcon = link ? GlobeHemisphereWestIcon : FileTextIcon;
        return (
          <Attachment key={`${name}-${index}`} size="sm" className="max-w-64" title={name}>
            <AttachmentMedia className="text-muted-foreground">
              <AttachmentIcon />
            </AttachmentMedia>
            <AttachmentContent>
              <AttachmentTitle>{link ? displayLink(name) : name}</AttachmentTitle>
            </AttachmentContent>
          </Attachment>
        );
      })}
      {hidden > 0 && (
        <Button
          variant="ghost"
          size="sm"
          shape="pill"
          className="text-muted-foreground"
          onClick={() => setExpanded(true)}
        >
          {leoLabels(labels).more(hidden)}
        </Button>
      )}
    </div>
  );
}

function LeoIssueChip({ issue, className }: { issue: NonNullable<LeoMessage["issue"]>; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex w-fit items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 typo-label-sm text-primary-text",
        className
      )}
    >
      {issue.icon && <issue.icon weight="bold" className="size-3" aria-hidden="true" />}
      {issue.label}
    </span>
  );
}

function LeoSteps({ steps, labels }: { steps: LeoStep[]; labels?: Partial<LeoLabels> }) {
  const text = leoLabels(labels);
  return (
    <ol data-slot="leo-steps" className="flex flex-col gap-2 pt-1">
      {steps.map((step) => (
        <li key={step.label} className="flex items-start gap-2 typo-body-sm text-muted-foreground">
          <span className="flex h-5 shrink-0 items-center">
            {step.status === "active" ? (
              <Spinner aria-label={step.label} className="size-3.5" />
            ) : (
              <CheckIcon weight="bold" className="size-3.5 text-success" aria-label={text.done} />
            )}
          </span>
          <span className={cn(step.status === "active" && "text-foreground")}>{step.label}</span>
        </li>
      ))}
    </ol>
  );
}

function LeoActions({
  message,
  disabled,
  onAction
}: {
  message: LeoMessage;
  disabled: boolean;
  onAction?: LeoActionHandler;
}) {
  return (
    <Replies data-slot="leo-actions" className="pt-1">
      {message.actions?.map((action) => {
        const ActionIcon = action.intent === "compose" ? action.icon ?? PencilSimpleIcon : action.icon;
        const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
          if (action.intent === "compose") {
            event.currentTarget
              .closest("[data-slot=leo-panel]")
              ?.querySelector<HTMLTextAreaElement>("[data-leo-composer]")
              ?.focus();
            return;
          }
          onAction?.(message, action);
        };
        const content = (
          <>
            {ActionIcon && <ActionIcon data-icon="inline-start" />}
            {action.label}
          </>
        );
        return action.primary ? (
          <Button key={action.id} shape="pill" disabled={disabled} onClick={onClick}>
            {content}
          </Button>
        ) : (
          <Reply
            key={action.id}
            quiet={action.intent === "compose" || action.subtle}
            disabled={disabled}
            onClick={onClick}
          >
            {content}
          </Reply>
        );
      })}
    </Replies>
  );
}

function LeoConversation({
  messages,
  status,
  thinkingSteps,
  onAction,
  contentClassName,
  anchorUserMessages = true,
  labels
}: {
  messages: LeoMessage[];
  status: LeoStatus;
  thinkingSteps: string[];
  onAction?: LeoActionHandler;
  contentClassName?: string;
  anchorUserMessages?: boolean;
  labels?: Partial<LeoLabels>;
}) {
  const text = leoLabels(labels);
  const end = React.useRef<HTMLDivElement>(null);
  const pinned = React.useRef(true);
  // Without anchors the conversation follows its end: new messages and text still revealing keep it scrolled down,
  // unless the user has scrolled up to read.
  React.useEffect(() => {
    if (anchorUserMessages) return;
    const content = end.current?.parentElement;
    const viewport = end.current?.closest<HTMLElement>("[data-slot=message-scroller-viewport]");
    if (!content || !viewport) return;
    const onScroll = () => {
      pinned.current = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 48;
    };
    const follow = new ResizeObserver(() => {
      if (pinned.current) viewport.scrollTop = viewport.scrollHeight;
    });
    viewport.addEventListener("scroll", onScroll, { passive: true });
    follow.observe(content);
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      follow.disconnect();
    };
  }, [anchorUserMessages]);
  const lastId = messages.at(-1)?.id;
  React.useEffect(() => {
    if (anchorUserMessages) return;
    const viewport = end.current?.closest<HTMLElement>("[data-slot=message-scroller-viewport]");
    if (!viewport) return;
    pinned.current = true;
    viewport.scrollTop = viewport.scrollHeight;
  }, [anchorUserMessages, lastId, status]);

  return (
    <MessageScrollerProvider autoScroll>
      <MessageScroller>
        <MessageScrollerViewport
          aria-label={text.conversation}
          className="no-scrollbar [scrollbar-width:none]! [scrollbar-gutter:auto]!"
        >
          <MessageScrollerContent className={cn("gap-6 py-4", contentClassName)}>
            {messages
              .map((message) =>
                message.title && message.answered
                  ? {
                      ...message,
                      title: undefined,
                      content: message.question ?? message.content,
                      question: undefined,
                      actions: undefined
                    }
                  : message
              )
              .map((message) => (
                <MessageScrollerItem
                  key={message.id}
                  messageId={message.id}
                  scrollAnchor={anchorUserMessages && message.role === "user"}
                >
                  {message.role === "user" ? (
                    <Message align="end">
                      <MessageContent className="items-end gap-2">
                        {message.attachments && (
                          <LeoAttachments names={message.attachments} align="end" labels={labels} />
                        )}
                        {message.content && (
                          <LeoUserBubble>
                            <p className="whitespace-pre-line">{message.content}</p>
                          </LeoUserBubble>
                        )}
                      </MessageContent>
                    </Message>
                  ) : message.title ? (
                    <div data-slot="leo-greeting" className="flex flex-col items-start gap-4 pt-6 pb-2">
                      <div className="flex flex-col gap-2">
                        <TextReveal as="h2" className="typo-h3">
                          {message.title}
                        </TextReveal>
                        {message.content && (
                          <p className="typo-body-sm whitespace-pre-line text-muted-foreground">{message.content}</p>
                        )}
                      </div>
                      <div className="flex w-full flex-col gap-2 border-t border-border pt-4">
                        {message.question && <p className="typo-label-md text-foreground">{message.question}</p>}
                        {message.actions && (
                          <LeoActions message={message} disabled={status === "thinking"} onAction={onAction} />
                        )}
                      </div>
                    </div>
                  ) : (
                    <Message>
                      <MessageAvatar className="self-start bg-transparent">
                        <LeoAvatar />
                      </MessageAvatar>
                      <MessageContent>
                        <Bubble variant="ghost">
                          <BubbleContent className="flex flex-col gap-2">
                            {message.flag && (
                              <span className="flex items-center gap-1.5 pt-1.5 typo-label-sm text-destructive">
                                <WarningIcon weight="bold" className="size-3.5" aria-hidden="true" />
                                {message.flag}
                              </span>
                            )}
                            {message.content && (
                              <TextReveal as="p" className="pt-1 typo-body-sm whitespace-pre-line">
                                {message.content}
                              </TextReveal>
                            )}
                            {message.question && (
                              <div
                                data-slot="leo-question"
                                className={cn(
                                  "relative flex flex-col items-start gap-1.5 ps-3 before:absolute before:inset-y-0.5 before:start-0 before:w-[1.5px] before:rounded-full before:bg-border",
                                  message.content ? "mt-1" : "mt-1.5"
                                )}
                              >
                                {message.issue && <LeoIssueChip issue={message.issue} />}
                                <TextReveal as="p" className="typo-body-sm whitespace-pre-line text-foreground">
                                  {message.question}
                                </TextReveal>
                              </div>
                            )}
                            {message.issue && !message.question && (
                              <LeoIssueChip issue={message.issue} className="mt-1.5" />
                            )}
                            {message.steps && <LeoSteps steps={message.steps} labels={labels} />}
                            {message.attachments && <LeoAttachments names={message.attachments} labels={labels} />}
                          </BubbleContent>
                        </Bubble>
                        {message.actions && !message.answered && (
                          <LeoActions message={message} disabled={status === "thinking"} onAction={onAction} />
                        )}
                      </MessageContent>
                    </Message>
                  )}
                </MessageScrollerItem>
              ))}
            {status === "thinking" && (
              <MessageScrollerItem messageId="leo-thinking">
                <LeoThinking steps={thinkingSteps} labels={labels} />
              </MessageScrollerItem>
            )}
            <div ref={end} aria-hidden="true" />
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton label={text.scrollToEnd} />
      </MessageScroller>
    </MessageScrollerProvider>
  );
}

function useLeoShortcut(onToggle: () => void) {
  const onToggleRef = React.useRef(onToggle);

  React.useEffect(() => {
    onToggleRef.current = onToggle;
  });

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "j" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      onToggleRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
}

function LeoEdgeGlow({ active }: { active: boolean }) {
  const { root } = useMotionScope<HTMLDivElement>(
    ({ root, reduced }) => {
      if (reduced || !active) return;
      animate(root, { opacity: [0.1, 0.35], duration: duration.slower, ease: ease.inOut, alternate: true, loop: true });
    },
    [active]
  );

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 start-0 hidden lg:block">
      <div
        ref={root}
        data-leo-glow=""
        className="absolute inset-y-[10%] -start-12 w-24 bg-linear-to-b from-transparent via-primary-400 to-secondary-400/0 opacity-0 blur-2xl will-change-[opacity]"
      />
    </div>
  );
}

function playEnter(root: HTMLElement) {
  const direction = getComputedStyle(root).direction === "rtl" ? -1 : 1;
  const pieces = root.querySelectorAll("[data-leo-reveal]");
  const glow = root.querySelector("[data-leo-glow]");
  utils.set(pieces, { opacity: 0 });
  const timeline = createTimeline({ defaults: { ease: ease.enter } })
    .add(root, { translateX: [`${100 * direction}%`, "0%"], duration: duration.slow }, 0)
    .add(
      pieces,
      { opacity: [0, 1], translateX: [24 * direction, 0], duration: duration.slow, delay: stagger(staggerStep.base) },
      160
    );
  if (glow) timeline.add(glow, { opacity: [0, 0.28, 0.1], duration: duration.slower * 1.4, ease: ease.standard }, 120);
  return timeline;
}

function playExit(root: HTMLElement, onComplete: () => void) {
  const direction = getComputedStyle(root).direction === "rtl" ? -1 : 1;
  return createTimeline({ defaults: { ease: ease.exit }, onComplete })
    .add(root.querySelectorAll("[data-leo-reveal]"), { opacity: 0, duration: duration.fast }, 0)
    .add(root, { translateX: `${100 * direction}%`, duration: duration.base, ease: "in(3)" }, 60);
}

type LeoPanelProps = {
  messages: LeoMessage[];
  status: LeoStatus;
  thinkingSteps?: string[];
  suggestions?: string[];
  placeholder?: string;
  conversation?: React.ReactNode;
  conversationClassName?: string;
  anchorUserMessages?: boolean;
  composerDisabled?: boolean;
  composerHidden?: boolean;
  attachAccept?: string;
  labels?: Partial<LeoLabels>;
  onSend: (text: string) => void;
  onAction?: LeoActionHandler;
  onAttach?: (files: File[]) => void;
  onAddLink?: () => void;
};

function LeoPanel({
  messages,
  status,
  labels,
  thinkingSteps = [leoLabels(labels).thinking],
  suggestions = [],
  placeholder,
  conversation,
  conversationClassName,
  anchorUserMessages,
  composerDisabled = false,
  composerHidden = false,
  attachAccept,
  onSend,
  onAction,
  onAttach,
  onAddLink,
  onClose,
  closeLabel = leoLabels(labels).close,
  closeHint,
  welcomeKey,
  surface = "card",
  className
}: LeoPanelProps & {
  onClose?: () => void;
  closeLabel?: string;
  closeHint?: React.ReactNode;
  welcomeKey?: React.Key;
  surface?: "card" | "bare";
  className?: string;
}) {
  const greeting =
    messages.length === 1 && messages[0]?.role === "leo" && messages[0].title && !messages[0].answered
      ? messages[0]
      : undefined;

  return (
    <section
      data-slot="leo-panel"
      data-surface={surface}
      aria-label={leoLabels(labels).panel}
      className={cn(
        "group/leo-panel relative flex h-full min-h-0 w-full flex-col gap-4 text-foreground",
        "data-[surface=card]:overflow-hidden data-[surface=card]:rounded-xl data-[surface=card]:border data-[surface=card]:bg-card data-[surface=card]:px-4 data-[surface=card]:pt-3 data-[surface=card]:pb-4",
        "data-[surface=bare]:px-5 data-[surface=bare]:py-5 sm:data-[surface=bare]:px-8 sm:data-[surface=bare]:py-7",
        className
      )}
    >
      {surface === "bare" && <LeoEdgeGlow active={status === "thinking"} />}

      <LeoPanelHeader
        data-leo-reveal
        onClose={onClose}
        closeLabel={closeLabel}
        closeHint={closeHint}
        labels={labels}
        className={cn(surface === "card" ? "-mx-4 -mt-3" : "h-auto min-h-9 border-b-0 ps-0 pe-0")}
      />

      <div data-leo-reveal className="relative min-h-0 flex-1">
        {conversation !== undefined ? (
          conversation
        ) : messages.length === 0 && status === "idle" ? (
          <LeoWelcome key={welcomeKey} labels={labels}>
            {suggestions.map((suggestion) => (
              <LeoWelcomePill key={suggestion} onClick={() => onSend(suggestion)}>
                {suggestion}
              </LeoWelcomePill>
            ))}
          </LeoWelcome>
        ) : greeting && status === "idle" ? (
          <LeoWelcome title={greeting.title} description={greeting.content} prompt={greeting.question}>
            {greeting.actions?.map((action) => (
              <LeoWelcomePill
                key={action.id}
                icon={action.icon}
                subtle={action.subtle}
                onClick={() => {
                  if (action.intent === "compose")
                    document.querySelector<HTMLTextAreaElement>("[data-leo-composer]")?.focus();
                  else onAction?.(greeting, action);
                }}
              >
                {action.label}
              </LeoWelcomePill>
            ))}
          </LeoWelcome>
        ) : (
          <LeoConversation
            messages={messages}
            status={status}
            thinkingSteps={thinkingSteps}
            onAction={onAction}
            contentClassName={conversationClassName}
            anchorUserMessages={anchorUserMessages}
            labels={labels}
          />
        )}
      </div>

      {!composerHidden && (
        <div data-leo-reveal>
          <LeoComposer
            disabled={status === "thinking" || composerDisabled}
            placeholder={placeholder}
            attachAccept={attachAccept}
            labels={labels}
            onSend={onSend}
            onAttach={onAttach}
            onAddLink={onAddLink}
          />
        </div>
      )}
    </section>
  );
}

const DOCK_QUERY = "(min-width: 64rem)";

// As wide as the panel, less the 16px the page keeps to the window's edge (344px in a 1440px window).
const leoDockClassName =
  "sticky top-(--app-header-height) h-[calc(100svh-var(--app-header-height))] shrink-0 [--leo-dock-width:calc(clamp(22rem,25vw,25rem)-1rem)]";
const leoDockContentClassName = "h-full w-(--leo-dock-width) pt-4 pb-4";

const shortcutHint = (label: string) => (
  <>
    {label}
    <KbdGroup>
      <Kbd>⌘</Kbd>
      <Kbd>J</Kbd>
    </KbdGroup>
  </>
);

function LeoDock({
  open,
  onOpenChange,
  appear,
  className,
  ...panel
}: LeoPanelProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appear?: boolean;
  className?: string;
}) {
  const text = leoLabels(panel.labels);
  return (
    <SidePanel
      open={open}
      appear={appear}
      className={cn(leoDockClassName, "hidden w-(--leo-dock-width) lg:block", className)}
      contentClassName={leoDockContentClassName}
    >
      <LeoPanel
        {...panel}
        surface="card"
        onClose={() => onOpenChange(false)}
        closeHint={shortcutHint(text.hide)}
        closeLabel={text.hide}
      />
    </SidePanel>
  );
}

function awaitingReply(messages: LeoMessage[]) {
  const last = messages.at(-1);
  return last?.role === "leo" && !last.answered && (last.question || last.actions?.length) ? last : undefined;
}

function LeoChat({
  messages,
  status,
  labels,
  thinkingSteps = [leoLabels(labels).thinking],
  suggestions = [],
  onSend,
  docked,
  ...panel
}: LeoPanelProps & {
  docked?: { open: boolean; onOpenChange: (open: boolean) => void };
}) {
  const text = leoLabels(labels);
  const [open, setOpen] = React.useState(false);
  const [openCount, setOpenCount] = React.useState(0);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const launcherRef = React.useRef<HTMLButtonElement>(null);
  const closingRef = React.useRef(false);
  const overlayId = React.useId();
  const wide = useMediaQuery(DOCK_QUERY);
  const dock = docked && wide ? docked : undefined;
  const overlayOpen = open && !dock;
  const expanded = dock ? dock.open : open;
  const waiting = !expanded && status === "idle" ? awaitingReply(messages) : undefined;
  const [dismissedId, setDismissedId] = React.useState<string>();
  const nudge = waiting && waiting.id !== dismissedId ? waiting : undefined;

  const show = React.useCallback(() => {
    setOpenCount((count) => count + 1);
    setOpen(true);
  }, []);

  const close = React.useCallback(() => {
    const overlay = overlayRef.current;
    if (!overlay || closingRef.current) return;
    const finish = () => {
      closingRef.current = false;
      setOpen(false);
      launcherRef.current?.focus();
    };
    if (prefersReducedMotion()) return finish();
    closingRef.current = true;
    playExit(overlay, finish);
  }, []);

  const toggle = () => {
    if (dock) dock.onOpenChange(!dock.open);
    else if (open) close();
    else show();
  };

  useLeoShortcut(toggle);

  React.useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlayOpen || !overlay) return;
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    if (window.matchMedia(MOBILE_TAKEOVER_QUERY).matches) root.style.overflow = "hidden";
    overlay.querySelector<HTMLTextAreaElement>("[data-leo-composer]")?.focus({ preventScroll: true });
    const timeline = prefersReducedMotion() ? undefined : playEnter(overlay);
    return () => {
      root.style.overflow = previousOverflow;
      timeline?.revert();
    };
  }, [overlayOpen]);

  return (
    <>
      <div
        ref={overlayRef}
        id={overlayId}
        role="dialog"
        aria-label={text.panel}
        hidden={!overlayOpen}
        data-slot="leo-overlay"
        className="pointer-events-none fixed inset-y-0 end-0 z-50 flex w-full text-foreground will-change-transform [--leo-panel-width:clamp(24rem,28vw,34rem)] lg:w-[calc(var(--leo-panel-width)+12rem)]"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            close();
          }
        }}
      >
        <div
          aria-hidden="true"
          style={featherStyle}
          className="pointer-events-none absolute inset-0 backdrop-blur-2xl lg:[mask-image:var(--leo-mask)] lg:rtl:[mask-image:var(--leo-mask-rtl)]"
        />
        <div
          aria-hidden="true"
          style={featherStyle}
          className="pointer-events-none absolute inset-0 bg-canvas/95 lg:bg-transparent lg:[background-image:var(--leo-tint)] lg:rtl:[background-image:var(--leo-tint-rtl)]"
        />
        <LeoPanel
          {...panel}
          labels={labels}
          messages={messages}
          status={status}
          thinkingSteps={thinkingSteps}
          suggestions={suggestions}
          onSend={onSend}
          onClose={close}
          closeHint={
            <>
              {text.closeOverlay} <Kbd>Esc</Kbd>
            </>
          }
          welcomeKey={openCount}
          surface="bare"
          className="pointer-events-auto ms-auto lg:w-(--leo-panel-width)"
        />
      </div>

      <div className="fixed end-5 bottom-5 z-40 flex flex-col items-end gap-3">
        {nudge && (
          <div
            key={nudge.id}
            data-slot="leo-nudge"
            className="relative w-72 rounded-2xl bg-card p-3 pe-10 text-start shadow-lg ring-1 ring-foreground/10 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2"
          >
            <button
              type="button"
              onClick={toggle}
              className="flex w-full flex-col items-start gap-1 text-start outline-none after:absolute after:inset-0 after:rounded-2xl focus-visible:after:ring-3 focus-visible:after:ring-ring/50"
            >
              <span className="flex items-center gap-1.5 typo-label-sm text-leo">
                <LeoGlyph className="size-3.5" title="" />
                {text.waiting}
              </span>
              <span className="line-clamp-3 typo-body-sm text-foreground">{nudge.question ?? nudge.content}</span>
            </button>
            <Button
              variant="ghost"
              size="icon-xs"
              shape="pill"
              aria-label={text.dismiss}
              className="absolute end-2 top-2 z-10 text-muted-foreground"
              onClick={() => setDismissedId(nudge.id)}
            >
              <XIcon />
            </Button>
          </div>
        )}
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                ref={launcherRef}
                type="button"
                aria-label={waiting ? text.askWaiting : text.ask}
                aria-expanded={expanded}
                aria-controls={dock ? undefined : overlayId}
                aria-keyshortcuts="Meta+J Control+J"
                data-open={expanded}
                onClick={toggle}
                className="relative flex size-14 items-center justify-center rounded-full bg-card text-leo shadow-lg ring-1 ring-foreground/10 transition-[scale,opacity] duration-300 outline-none hover:scale-105 focus-visible:ring-3 focus-visible:ring-ring/50 active:scale-95 data-[open=true]:pointer-events-none data-[open=true]:scale-75 data-[open=true]:opacity-0"
              />
            }
          >
            <LeoGlyph className="size-7" title="" />
            {waiting && (
              <span
                aria-hidden="true"
                data-slot="leo-waiting"
                className="absolute end-0.5 top-0.5 size-3 rounded-full bg-leo ring-2 ring-card"
              />
            )}
          </TooltipTrigger>
          <TooltipContent side="left" className="flex items-center gap-2">
            {shortcutHint(text.ask)}
          </TooltipContent>
        </Tooltip>
      </div>
    </>
  );
}

export {
  defaultLeoLabels,
  leoComposerClassNames,
  leoDockClassName,
  leoDockContentClassName,
  LeoDisclaimer,
  LeoPanelHeader,
  LeoUserBubble,
  LeoWelcome,
  LeoWelcomePill,
  LeoChat,
  LeoDock,
  LeoPanel,
  LeoAvatar,
  LeoConversation,
  LeoThinking,
  LeoSteps,
  LeoAttachments,
  LeoIssueChip,
  LeoComposer,
  LeoActions,
  type LeoAction,
  type LeoLabels,
  type LeoMessage,
  type LeoStatus,
  type LeoStep
};
